import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { 
  Users, BookOpen, Award, TrendingUp, 
  Clock, CheckCircle, XCircle, Activity 
} from 'lucide-react';
import api from '../../services/api';
import Navbar from '../../components/Navbar';
import Loading from '../../components/Loading';
import { 
  AreaChart, Area, BarChart, Bar, XAxis, YAxis, 
  CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell 
} from 'recharts';

const Dashboard = () => {
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [analytics, setAnalytics] = useState(null);
  const [recentActivity, setRecentActivity] = useState([]);

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      const [statsRes, analyticsRes] = await Promise.all([
        api.get('/admin/dashboard'),
        api.get('/admin/analytics?days=30')
      ]);
      setStats({
  totalUsers: Number(statsRes.data.stats?.users?.total_users || 0),
  newUsersToday: Number(statsRes.data.stats?.users?.new_users_week || 0),

  activeLearners: Number(
    statsRes.data.stats?.course?.enrolled_users || 0
  ),

  totalSertifikalar: Number(
    statsRes.data.stats?.certificates?.total_certificates || 0
  ),

  certificatesToday: Number(
    statsRes.data.stats?.certificates?.certificates_week || 0
  ),

  completionRate: Number(
    statsRes.data.stats?.course?.average_completion || 0
  ),

  completedUsers: Number(
    statsRes.data.stats?.course?.completed_users || 0
  ),

  inProgressUsers: Number(
    statsRes.data.stats?.course?.in_progress_users || 0
  ),

  notStartedUsers: 0,
  activityChart: statsRes.data.activityChart || []
});
      setRecentActivity([]);
      setAnalytics(analyticsRes.data);
    } catch (err) {
      console.error('Failed to load dashboard:', err);
    } finally {
      setLoading(false);
    }
  };

  if (loading) return <Loading />;

  const COLORS = ['#C62828', '#059669', '#d97706', '#dc2626'];

  const statusData = [
    { name: 'Tamamlandı', value: stats?.completedUsers || 0, color: '#059669' },
    { name: 'Devam Ediyor', value: stats?.inProgressUsers || 0, color: '#d97706' },
    { name: 'Başlanmadı', value: stats?.notStartedUsers || 0, color: '#94a3b8' }
  ];

  return (
    <div className="min-h-screen" style={{ backgroundColor: '#f8fafc' }}>
      <Navbar />
      
      <div className="container py-8">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold">Yönetim Paneli</h1>
            <p className="text-gray">Platform faaliyetleri ve istatistikleri</p>
          </div>
          <Link to="/admin/users" className="btn btn-primary">
            <Users size={18} /> Kullanıcı Yönetimi
          </Link>
        </div>

        {/* Stats Cards */}
        <div className="grid mb-6" style={{ gridTemplateColumns: 'repeat(4, 1fr)', gap: '1.5rem' }}>
          <StatCard 
            icon={<Users size={24} />} 
            label="Toplam Kullanıcı" 
            value={stats?.totalUsers || 0}
            change={`+${stats?.newUsersToday || 0} bugün`}
            color="#C62828"
            bg="#e0f2fe"
          />
          <StatCard 
            icon={<BookOpen size={24} />} 
            label="Aktif Kullanıcılar" 
            value={stats?.activeLearners || 0}
            change="Son 7 Gün"
            color="#059669"
            bg="#d1fae5"
          />
          <StatCard 
            icon={<Award size={24} />} 
            label="Oluşturulan Sertifikalar" 
            value={stats?.totalSertifikalar || 0}
            change={`${stats?.certificatesToday || 0} bugün`}
            color="#d97706"
            bg="#fef3c7"
          />
          <StatCard 
            icon={<TrendingUp size={24} />} 
            label="Tamamlanma Oranı" 
            value={`${stats?.completionRate || 0}%`}
            change="Genel"
            color="#7c3aed"
            bg="#ede9fe"
          />
        </div>

        <div className="grid" style={{ gridTemplateColumns: '2fr 1fr', gap: '1.5rem' }}>
          {/* Activity Chart */}
          <div className="card">
            <div className="card-header">
              <h2 className="text-lg font-semibold">Son 30 Gün Kullanıcı Aktivitesi</h2>
            </div>
            <div className="card-body">
              <ResponsiveContainer width="100%" height={300}>
                <AreaChart data={analytics?.userActivity || []}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                  <XAxis dataKey="date" stroke="#94a3b8" fontSize={12} />
                  <YAxis stroke="#94a3b8" fontSize={12} />
                  <Tooltip />
                  <Area 
                    type="monotone" 
                    dataKey="activeUsers" 
                    stroke="#C62828" 
                    fill="#e0f2fe" 
                    name="Aktif Kullanıcılar"
                  />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </div>

          {/* Status Distribution */}
          <div className="card">
            <div className="card-header">
              <h2 className="text-lg font-semibold">Kullanıcı Durumu</h2>
            </div>
            <div className="card-body">
              <ResponsiveContainer width="100%" height={200}>
                <PieChart>
                  <Pie
                    data={statusData}
                    cx="50%"
                    cy="50%"
                    innerRadius={60}
                    outerRadius={80}
                    paddingAngle={5}
                    dataKey="value"
                  >
                    {statusData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
              <div className="flex justify-center gap-4 mt-4">
                {statusData.map((item, index) => (
                  <div key={index} className="flex items-center gap-2">
                    <div style={{ width: '12px', height: '12px', borderRadius: '50%', backgroundColor: item.color }}></div>
                    <span className="text-sm text-gray">{item.name}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        <div className="grid mt-6" style={{ gridTemplateColumns: '1fr 1fr', gap: '1.5rem' }}>
          {/* Son Kayıtlar */}
          <div className="card">
            <div className="card-header">
              <h2 className="text-lg font-semibold">Son Kayıtlar</h2>
              <Link to="/admin/users" className="text-sm text-primary">Tümünü Gör</Link>
            </div>
            <div className="card-body p-0">
              {stats?.recentUsers?.length > 0 ? (
                <table className="table">
                  <thead>
                    <tr>
                      <th>Ad Soyad</th>
                      <th>E-Posta</th>
                      <th>Kayıt Tarihi</th>
                    </tr>
                  </thead>
                  <tbody>
                    {stats.recentUsers.slice(0, 5).map((user) => (
                      <tr key={user.id}>
                        <td>
                          <Link to={`/admin/users/${user.id}`} className="font-medium">
                            {user.name}
                          </Link>
                        </td>
                        <td className="text-gray">{user.email}</td>
                        <td className="text-gray text-sm">
                          {new Date(user.created_at).toLocaleDateString()}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              ) : (
                <div className="p-6 text-center text-gray">Henüz kayıt bulunmuyor</div>
              )}
            </div>
          </div>

          {/* Son Tamamlamalar */}
          <div className="card">
            <div className="card-header">
              <h2 className="text-lg font-semibold">Son Tamamlamalar</h2>
              <Link to="/admin/analytics" className="text-sm text-primary">Analitikleri Gör</Link>
            </div>
            <div className="card-body p-0">
              {stats?.recentCompletions?.length > 0 ? (
                <table className="table">
                  <thead>
                    <tr>
                      <th>Kullanıcı</th>
                      <th>Eğitim</th>
                      <th>Puan</th>
                    </tr>
                  </thead>
                  <tbody>
                    {stats.recentCompletions.slice(0, 5).map((completion, index) => (
                      <tr key={index}>
                        <td className="font-medium">{completion.user_name}</td>
                        <td className="text-gray">{completion.course_title}</td>
                        <td>
                          <span className="badge badge-success">{completion.score}%</span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              ) : (
                <div className="p-6 text-center text-gray">Henüz tamamlanan eğitim bulunmuyor</div>
              )}
            </div>
          </div>
        </div>

        {/* Quick Actions */}
        <div className="grid mt-6" style={{ gridTemplateColumns: 'repeat(4, 1fr)', gap: '1rem' }}>
          <Link to="/admin/users" className="card p-4 text-center" style={{ textDecoration: 'none' }}>
            <Users size={32} color="#C62828" style={{ margin: '0 auto 0.5rem' }} />
            <p className="font-medium">Kullanıcı Yönetimi</p>
            <p className="text-xs text-gray">{stats?.totalUsers || 0} kullanıcı</p>
          </Link>
          <Link to="/admin/analytics" className="card p-4 text-center" style={{ textDecoration: 'none' }}>
            <Activity size={32} color="#059669" style={{ margin: '0 auto 0.5rem' }} />
            <p className="font-medium">Analitikleri Gör</p>
            <p className="text-xs text-gray">Detaylı Raporlar</p>
          </Link>
          <Link to="/admin/analytics" className="card p-4 text-center" style={{ textDecoration: 'none' }}>
            <Award size={32} color="#d97706" style={{ margin: '0 auto 0.5rem' }} />
            <p className="font-medium">Sertifikalar</p>
            <p className="text-xs text-gray">{stats?.totalSertifikalar || 0} oluşturuldu</p>
          </Link>
          <Link to="/admin/analytics" className="card p-4 text-center" style={{ textDecoration: 'none' }}>
            <TrendingUp size={32} color="#7c3aed" style={{ margin: '0 auto 0.5rem' }} />
            <p className="font-medium">Veri Dışa Aktar</p>
            <p className="text-xs text-gray">Raporları İndir</p>
          </Link>
        </div>
      </div>
    </div>
  );
};

const StatCard = ({ icon, label, value, change, color, bg }) => (
  <div className="card">
    <div className="card-body p-5">
      <div className="flex items-start justify-between">
        <div>
          <p className="text-gray text-sm mb-1">{label}</p>
          <p className="text-3xl font-bold">{value}</p>
          <p className="text-xs text-gray mt-1">{change}</p>
        </div>
        <div style={{ 
          width: '48px', 
          height: '48px', 
          borderRadius: '12px', 
          backgroundColor: bg,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: color
        }}>
          {icon}
        </div>
      </div>
    </div>
  </div>
);

export default Dashboard;
